/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { use } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';
import type { ProductMapResult } from '@/lib/product/product-map';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useBasket } from '@/providers/basket';
import { useCurrency } from '@/providers/currency';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import { formatCurrency } from '@/lib/currency';
import { Typography } from '@/components/typography';
import { getDeliveryShipment } from '@/extensions/bopis/lib/basket-utils';
import {
    DELIVERY_GROUP_LABEL_KEYS,
    bucketItemsByDeliveryMethod,
    flattenDeliveryRowItems,
    formatDeliveryDate,
    getGroupDeliveryDate,
    getProductItemStringAttr,
    resolveProductItemImage,
    splitDeliveryRowsByBackorderStatus,
    type DeliveryMethod,
} from '@/lib/checkout/delivery-group-display';
import { Check, Truck } from 'lucide-react';
import { getDisplayVariationValues, type EnrichedProductItem } from '@/lib/product/product-utils';
import ProductPrice from '@/components/product-price';
import CartLinePromoCallouts from '@/components/product-price/cart-line-promo-callouts';
import OrderReviewShipTo from './order-review-shipto';
import OrderReviewBillTo from './order-review-billto';
import OrderReviewPaymentMethod from './order-review-paymentmethod';
import { getMerchandiseProductItems } from '@/lib/extend-basket-utils';
import {
    buildKitGroupByItemId,
    getKitDisplaySummary,
    getKitGroupPricing,
    getKitGroups,
    type KitBasketFields,
    type KitGroup,
    type KitProductFields,
} from '@/lib/kit-utils';
import { aggregateKitAtpFields, getAtpItemFields } from '@/lib/atp/atp-fields';
import BackorderNotice from '@/components/delivery-estimate/backorder-notice';
import { KitItemVariations } from '@/components/product-kit/kit-item-variations';
import KitGroupImage from '@/components/product-kit/kit-group-image';
import { DynamicImage } from '@/components/dynamic-image';

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];
type DeliveryRow =
    | { kind: 'item'; item: ProductItem }
    | { kind: 'kit'; group: KitGroup; componentItems: ProductItem[] };

interface OrderReviewItemsProps {
    /** Optional class name for the outer container. */
    className?: string;
    /**
     * Catalog product data keyed by basket item id (the checkout loader's
     * `productMap`). Required to resolve line-item thumbnails — basket
     * `productItems` do not carry image URLs themselves. Resolved within the
     * caller's Suspense boundary.
     */
    productMapPromise?: Promise<ProductMapResult>;
    /**
     * Promotions map keyed by promotionId. When provided, the promotion's
     * customer-facing offerMsg/calloutMsg is preferred over the basket-level
     * adjustment text in `CartLinePromoCallouts`. Resolved within the caller's
     * Suspense boundary; omit in tests/stories that don't assert promo copy.
     */
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
}

/**
 * CHECKOUT-14 — Read-only order-review item list.
 *
 * Renders the delivery shipment's productItems bucketed by `c_deliveryMethod`
 * (HD → DS → DS3PL) so the terminal "Review your order" state mirrors the
 * grouping the shopper saw on the shipping step (CHECKOUT-10) and the legacy
 * SiteGenesis OPC summary. Unlike `<DeliveryGroups/>` this view is purely
 * informational — no method toggle, no per-line "DELIVERY OPTIONS" banner, no
 * Continue affordance. Editing happens via the existing per-step Edit links.
 *
 * BOPIS items (different `shipmentId`) are intentionally omitted — they are
 * already surfaced in the pickup step + `<MyCart/>`. Returns `null` when there
 * is no delivery shipment (all-pickup basket) so nothing renders for those
 * orders.
 *
 * NOTE(images): basket `productItems` carry no image URL, so we resolve each
 * line's thumbnail by joining against the loader's `productMap` and reading the
 * catalog `imageGroups` (shared `resolveProductItemImage`, same path as
 * `<MyCart/>` and `<DeliveryGroups/>`). Fixed-size, below the LCP — uses
 * `<DynamicImage>` with equal widths and heights so DIS crops to a square.
 */
export default function OrderReviewItems({ className, productMapPromise, promotionsPromise }: OrderReviewItemsProps) {
    const { t, i18n } = useTranslation('checkout');
    const basket = useBasket();
    const currency = useCurrency();
    const config = useConfig<AppConfig>();
    const customerProfile = useCustomerProfile();
    // Products only — a degraded map renders the review rows from basket fields alone
    // (name, quantity, price), which is enough to review and place the order.
    const productMap = productMapPromise ? use(productMapPromise).products : undefined;
    const promotions = promotionsPromise ? use(promotionsPromise) : undefined;

    const deliveryShipment = getDeliveryShipment(basket);
    if (!basket || !deliveryShipment) {
        return null;
    }

    // INT-02 — Drop Extend warranty PLIs from the post-checkout review.
    // Filter first, then narrow by `shipmentId` so a warranty PLI that
    // happens to share the delivery shipment can never leak into the list.
    const deliveryItems = getMerchandiseProductItems(basket.productItems).filter(
        (item) => item.shipmentId === deliveryShipment.shipmentId
    );
    const { buckets, visibleGroups } = bucketItemsByDeliveryMethod(deliveryItems);
    if (visibleGroups.length === 0) {
        return null;
    }
    const kitGroupByItemId = buildKitGroupByItemId(getKitGroups(basket as KitBasketFields | undefined));
    const findKitMasterProduct = (group: KitGroup) => {
        if (!group.masterId) return undefined;
        return (
            productMap?.[group.masterId] ||
            Object.values(productMap ?? {}).find(
                (product) => product.id === group.masterId || product.master?.masterId === group.masterId
            )
        );
    };

    const buildDeliveryRows = (items: ProductItem[]): DeliveryRow[] => {
        const rows: DeliveryRow[] = [];
        const renderedKitNumbers = new Set<string>();

        for (const item of items) {
            const group = item.itemId ? kitGroupByItemId.get(item.itemId) : undefined;
            if (group) {
                if (renderedKitNumbers.has(group.kitNumber)) continue;
                renderedKitNumbers.add(group.kitNumber);
                rows.push({
                    kind: 'kit',
                    group,
                    componentItems: items.filter((candidate) =>
                        Boolean(candidate.itemId && group.itemIds.includes(candidate.itemId))
                    ),
                });
                continue;
            }
            if ((item as { c_partOfKitNumber?: string | null }).c_partOfKitNumber) continue;
            rows.push({ kind: 'item', item });
        }

        return rows;
    };

    const billingAddress = basket.billingAddress;
    const paymentInstrument = basket.paymentInstruments?.[0];
    const customer = customerProfile?.customer;
    const contactEmail = customer?.login || customer?.email || basket.customerInfo?.email || undefined;

    const basketRecord = basket as unknown as Record<string, unknown>;
    // SSF-374 — white-glove is a SINGLE flat fee (legacy parity), applied once
    // per basket, only when the basket contains at least one true 3PBB item.
    // `c_whiteGloveTotal` (from `ShippingCostHelper.computeShippingComponents`
    // via `BasketEnrichmentHelper`) is the exact amount the backend adds to
    // `basket.shippingTotal`, so the review-step DS3PL row and the Order
    // Summary read ONE number and can never drift. Do NOT compute
    // qty × site-pref-fee locally: that path ignores the 3PBB predicate
    // (mis-charges non-3PBB DS3PL items) and wrongly multiplies by quantity.
    const whiteGloveTotal = typeof basketRecord.c_whiteGloveTotal === 'number' ? basketRecord.c_whiteGloveTotal : 0;
    const currentHdShippingType: 'HD' | 'THD' = basketRecord.c_hdShippingType === 'THD' ? 'THD' : 'HD';
    const hdFee = typeof basketRecord.c_hdFee === 'number' ? basketRecord.c_hdFee : null;
    const thdFee = typeof basketRecord.c_thdFee === 'number' ? basketRecord.c_thdFee : null;

    /**
     * Row list shared by every block (HD's one block, and each DS/DS3PL
     * group's Available and/or Backordered block). `sectionKey` is only used
     * for the `key` fallback on itemless-id rows — every existing testid here
     * (`order-review-item-*`, `order-review-kit-*`) is productId/kitNumber-keyed
     * and never included `group`, so this split doesn't change any of them.
     *
     * `showPerRowNotice` gates the per-item/-kit `<BackorderNotice>`. HD and
     * every Backordered DS/DS3PL block pass `false` — they show ONE
     * group-level plural notice above the item list instead
     * (`groupLevelNoticeSourceItem` on `renderGroupBlock`, matching HD's
     * presentation). Available/no-split blocks pass `true`, but since none
     * of their rows are backordered `<BackorderNotice>` self-gates to
     * nothing — this only matters if a row later becomes backordered
     * without a re-split.
     */
    const renderRowList = (rows: DeliveryRow[], sectionKey: string, showPerRowNotice: boolean) => (
        <ul className="flex flex-col rounded-md border border-border p-3 divide-y divide-border">
            {rows.map((row, idx) => {
                if (row.kind === 'kit') {
                    const kitDisplayItem = row.componentItems[0];
                    const kitMasterProduct = findKitMasterProduct(row.group);
                    const kitVisualItem = kitMasterProduct
                        ? (kitMasterProduct as EnrichedProductItem)
                        : kitDisplayItem?.itemId && productMap?.[kitDisplayItem.itemId]
                          ? ({
                                ...productMap[kitDisplayItem.itemId],
                                ...kitDisplayItem,
                            } as EnrichedProductItem)
                          : (kitDisplayItem as EnrichedProductItem | undefined);
                    const kitVariationItems = row.componentItems.map((component) => {
                        const mappedProduct = component.itemId ? productMap?.[component.itemId] : undefined;
                        return mappedProduct
                            ? ({ ...mappedProduct, ...component } as EnrichedProductItem)
                            : (component as EnrichedProductItem);
                    });
                    const kitTitle = row.group.name || row.componentItems[0]?.productName || 'Product';
                    const kitPricing = getKitGroupPricing(
                        row.group,
                        row.componentItems,
                        kitMasterProduct as KitProductFields | undefined
                    );
                    // Synthetic basket-line shape so <ProductPrice> prices the
                    // kit roll-up through the same getPriceData basket-item
                    // branch every non-kit row uses (see standard
                    // `mergedForPricing` below). quantity: 1 because both totals
                    // already reflect the full kit instance count
                    // (row.group.quantity), so no further multiplication is
                    // needed.
                    const kitPriceItem = {
                        itemId: row.group.kitNumber,
                        basePrice: kitPricing.originalLineTotal,
                        price: kitPricing.lineTotal,
                        quantity: 1,
                    } as ShopperBasketsV2.schemas['ProductItem'];
                    const kitDisplay = getKitDisplaySummary(row.group, row.componentItems);

                    return (
                        <li
                            key={`kit-${row.group.kitNumber}`}
                            className="flex flex-col pb-3 last:pb-0 mb-3 last:mb-0"
                            data-testid={`order-review-kit-${row.group.masterId ?? row.group.kitNumber}`}>
                            <div className="flex items-start gap-3">
                                {kitVisualItem ? (
                                    <KitGroupImage
                                        productItem={kitVisualItem}
                                        title={kitTitle}
                                        widths={[64, 128]}
                                        containerClassName="size-16 shrink-0 overflow-hidden rounded-md"
                                    />
                                ) : (
                                    <div className="size-16 shrink-0 overflow-hidden rounded-md bg-muted" />
                                )}
                                <div className="min-w-0 flex-1 md:flex items-start gap-3 justify-between">
                                    <div className="flex-1 min-w-0">
                                        <Typography
                                            variant="p"
                                            className="truncate text-sm font-medium leading-tight text-foreground">
                                            {kitTitle}
                                        </Typography>
                                        {/* Item ID — mirrors the standard non-kit row
                                            below so kits and standalone products
                                            expose a comparable identifier on the
                                            Review step. Anchored on the kit master id
                                            (`row.group.masterId`) with a fallback to
                                            the first component's `productId` for the
                                            (unlikely) stale-basket case where the
                                            master id is missing. */}
                                        {(row.group.masterId ?? row.componentItems[0]?.productId) ? (
                                            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                                <div className="inline-flex items-center gap-1.5">
                                                    <span>{t('confirmation.summaryLabels.item')}</span>
                                                    <span aria-hidden="true">•</span>
                                                    <span>
                                                        {row.group.masterId ?? row.componentItems[0]?.productId}
                                                    </span>
                                                </div>
                                            </div>
                                        ) : null}
                                        <KitItemVariations
                                            items={kitVariationItems}
                                            masterProduct={kitMasterProduct}
                                            kitTitle={kitTitle}
                                        />
                                        <Typography variant="small" className="text-xs text-muted-foreground mt-1">
                                            {`${t('shipping.itemCount', { count: kitDisplay.quantity })} · ${t('confirmation.summaryLabels.includesCount', { count: kitDisplay.includesCount })}`}
                                        </Typography>
                                    </div>
                                    <div
                                        className="shrink-0 flex flex-col md:items-end gap-0.5"
                                        data-testid={`order-review-kit-total-${row.group.masterId ?? row.group.kitNumber}`}>
                                        <ProductPrice
                                            product={kitPriceItem}
                                            currency={currency}
                                            type="total"
                                            quantity={1}
                                            hidePromo
                                            hasDiscount={kitPricing.hasSavings}
                                            className="flex items-baseline gap-1.5 text-sm font-medium text-foreground"
                                            listPriceProps={{ className: 'text-sm font-normal' }}
                                        />
                                        <CartLinePromoCallouts
                                            priceAdjustments={kitPricing.promoAdjustments}
                                            promotions={promotions}
                                        />
                                    </div>
                                </div>
                            </div>
                            {showPerRowNotice ? (
                                <BackorderNotice
                                    productItem={aggregateKitAtpFields(row.componentItems)}
                                    className="mt-2"
                                />
                            ) : null}
                        </li>
                    );
                }

                const item = row.item;
                const productName = item.productName || 'Product';
                const { src: imageUrl, alt: imageAlt } = resolveProductItemImage(item, productMap, config);
                const quantity = item.quantity ?? 1;
                const productData = item.itemId ? productMap?.[item.itemId] : undefined;
                const displayVariationValues = getDisplayVariationValues(
                    (item.variationAttributes as ShopperProducts.schemas['VariationAttribute'][]) ??
                        productData?.variationAttributes ??
                        [],
                    (item.variationValues as Record<string, string> | undefined) ?? productData?.variationValues ?? {}
                );
                const variationEntries = Object.entries(displayVariationValues);
                const mergedForPricing = productData
                    ? ({
                          ...productData,
                          ...item,
                      } as ShopperBasketsV2.schemas['ProductItem'] & Partial<ShopperProducts.schemas['Product']>)
                    : item;
                return (
                    <li
                        key={item.itemId ?? `${sectionKey}-${idx}`}
                        className="flex flex-col pb-3 last:pb-0 mb-3 last:mb-0"
                        data-testid={`order-review-item-${item.productId ?? idx}`}>
                        <div className="flex items-start gap-3">
                            <div className="size-16 shrink-0 overflow-hidden rounded-md bg-product-image">
                                {imageUrl ? (
                                    <DynamicImage
                                        src={imageUrl}
                                        alt={imageAlt}
                                        widths={[64, 128]}
                                        heights={[64, 128]}
                                        loading="lazy"
                                        className="size-full object-cover object-center"
                                        imageProps={{ width: 128, height: 128, decoding: 'async' }}
                                    />
                                ) : null}
                            </div>
                            <div className="min-w-0 flex-1 md:flex items-start gap-3 justify-between">
                                <div className="flex-1 min-w-0">
                                    <Typography
                                        variant="p"
                                        className="truncate text-sm font-medium leading-tight text-foreground">
                                        {productName}
                                    </Typography>
                                    {item.productId && (
                                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                            <div className="inline-flex items-center gap-1.5">
                                                <span>{t('confirmation.summaryLabels.item')}</span>
                                                <span aria-hidden="true">•</span>
                                                <span>{item.productId}</span>
                                            </div>
                                        </div>
                                    )}
                                    {variationEntries.length > 0 && (
                                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                            {variationEntries.map(([name, value], index) => (
                                                <div key={name} className="inline-flex items-center gap-1.5">
                                                    <span>{value}</span>
                                                    {index < variationEntries.length - 1 ? (
                                                        <span aria-hidden="true">•</span>
                                                    ) : null}
                                                </div>
                                            ))}
                                            <span className="sr-only">
                                                {variationEntries
                                                    .map(([name, value]) => `${name}: ${value}`)
                                                    .join(', ')}
                                            </span>
                                        </div>
                                    )}
                                    <Typography variant="small" className="text-xs text-muted-foreground mt-1">
                                        {t('shipping.itemCount', { count: quantity })}
                                    </Typography>
                                </div>
                                <div
                                    className="shrink-0 flex flex-col md:items-end gap-0.5"
                                    data-testid={`order-review-item-total-${item.productId ?? idx}`}>
                                    <ProductPrice
                                        product={mergedForPricing}
                                        currency={currency}
                                        type="total"
                                        quantity={quantity}
                                        hidePromo
                                        hasDiscount={!!item.priceAdjustments?.length}
                                        className="flex items-baseline gap-1.5 text-sm font-medium text-foreground"
                                        listPriceProps={{ className: 'text-sm font-normal' }}
                                    />
                                    <CartLinePromoCallouts
                                        priceAdjustments={item.priceAdjustments}
                                        promotions={promotions}
                                    />
                                </div>
                            </div>
                        </div>
                        {showPerRowNotice ? <BackorderNotice productItem={item} className="mt-2" /> : null}
                    </li>
                );
            })}
        </ul>
    );

    /**
     * One self-contained group block — heading, method-info row, and item
     * list. Used for HD's single block and each DS/DS3PL
     * Available/Backordered block.
     */
    const renderGroupBlock = (params: {
        group: DeliveryMethod;
        sectionKey: string;
        rows: DeliveryRow[];
        deliveryDate: string | null;
        showPerRowNotice: boolean;
        /** Source item for the single group-level notice (HD, or a DS/DS3PL Backordered block). */
        groupLevelNoticeSourceItem?: ProductItem;
        /**
         * Whether the group-level notice uses plural wording ("These items…").
         * HD always passes `true` (unchanged legacy behavior — HD's notice
         * covers the whole truck shipment regardless of row count). DS/DS3PL
         * passes `rows.length > 1` so a single-product Backordered block uses
         * the singular title ("This item…").
         */
        groupLevelNoticePlural?: boolean;
    }) => {
        const {
            group,
            sectionKey,
            rows,
            deliveryDate,
            showPerRowNotice,
            groupLevelNoticeSourceItem,
            groupLevelNoticePlural = true,
        } = params;
        const headingId = `order-review-group-${sectionKey}-heading`;
        const groupLabel = t(DELIVERY_GROUP_LABEL_KEYS[group]);

        const methodName =
            group === 'HD'
                ? currentHdShippingType === 'THD'
                    ? t('shipping.method.doorstep')
                    : t('shipping.method.whiteGlove')
                : group === 'DS'
                  ? t('shipping.method.groundShipping')
                  : t('shipping.method.whiteGlove');

        const methodDescription =
            group === 'HD' && currentHdShippingType !== 'THD'
                ? t('shipping.method.whiteGloveReviewDescription')
                : t('shipping.method.standardReviewDescription');

        const assemblyLabel =
            group === 'HD' && currentHdShippingType !== 'THD'
                ? t('shipping.method.assemblyIncluded')
                : t('shipping.method.assemblyRequired');

        const fulfillerName = flattenDeliveryRowItems(rows)
            .map((item) => getProductItemStringAttr(item, 'c_fulfillerDisplayName'))
            .find(Boolean);

        const rawFee =
            group === 'HD'
                ? currentHdShippingType === 'THD'
                    ? thdFee
                    : hdFee
                : group === 'DS'
                  ? (deliveryShipment.shippingMethod?.price ?? null)
                  : // DS3PL — `c_whiteGloveTotal` is the backend-computed
                    // flat fee. 0 = no 3PBB item in the basket, which is a
                    // definite $0 → render "FREE" (matches Order Summary),
                    // NOT the "feeCalculating" placeholder the `null` branch
                    // uses for the not-yet-resolved HD case.
                    whiteGloveTotal;

        const methodPrice =
            rawFee === null
                ? t('shipping.method.feeCalculating')
                : rawFee <= 0
                  ? t('shipping.free')
                  : formatCurrency(rawFee, i18n.language, currency);

        return (
            <div
                key={sectionKey}
                role="group"
                aria-labelledby={headingId}
                data-testid={`order-review-group-${sectionKey}`}>
                <h3 id={headingId} className="mb-3 text-sm text-foreground" data-testid={headingId}>
                    {deliveryDate && (
                        <>
                            <span className="font-bold">
                                {t('confirmation.summaryLabels.arriving')} {deliveryDate}
                            </span>
                            <span aria-hidden="true"> · </span>
                        </>
                    )}
                    <span>{groupLabel}</span>
                    <span aria-hidden="true"> · </span>
                    <span>{methodPrice}</span>
                </h3>
                <p className="mb-3 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="flex items-center gap-1">
                        <Truck aria-hidden="true" size={14} className="shrink-0" />
                        <span>{methodName}</span>
                        <span aria-hidden="true">·</span>
                        <span>{methodDescription}</span>
                    </span>
                    <span className="flex items-center gap-1">
                        <Check aria-hidden="true" size={14} className="shrink-0" />
                        <span>{assemblyLabel}</span>
                    </span>
                    {fulfillerName && (
                        <span className="flex items-center gap-1">
                            <Check aria-hidden="true" size={14} className="shrink-0" />
                            <span>{t('shipping.fulfilledBy', { source: fulfillerName })}</span>
                        </span>
                    )}
                </p>
                {groupLevelNoticeSourceItem ? (
                    <BackorderNotice
                        productItem={groupLevelNoticeSourceItem}
                        plural={groupLevelNoticePlural}
                        className="mb-3"
                    />
                ) : null}
                {renderRowList(rows, sectionKey, showPerRowNotice)}
            </div>
        );
    };

    /**
     * HD's one-and-only block — untouched by the DS/DS3PL backorder split
     * (Ashley Home Delivery ships as one scheduled truck shipment regardless
     * of any line's backorder status). Keeps the legacy single group-level
     * plural `<BackorderNotice>` and omits "Arriving {date}" when any line is
     * backordered — no per-row notices are added.
     */
    const renderHdBlock = () => {
        const items = buckets.HD;
        const rows = buildDeliveryRows(items);
        const backorderSourceItem = items.find((item) => getAtpItemFields(item).isOnBackOrder);
        const deliveryDate = backorderSourceItem
            ? null
            : formatDeliveryDate(getGroupDeliveryDate('HD', items, deliveryShipment), i18n.language);
        return renderGroupBlock({
            group: 'HD',
            sectionKey: 'hd',
            rows,
            deliveryDate,
            showPerRowNotice: false,
            groupLevelNoticeSourceItem: backorderSourceItem,
        });
    };

    /**
     * Splits a DS/DS3PL group's rows by backorder status and returns 1 or 2
     * blocks: a single unsplit block when nothing is backordered (no behavior
     * change — date always shown), otherwise up to two blocks — Available
     * first (only when non-empty), Backordered second (only when non-empty).
     *
     * The Backordered block matches HD's presentation: ONE group-level
     * `<BackorderNotice>` above the item list (`showPerRowNotice: false` +
     * `groupLevelNoticeSourceItem`) rather than a notice under every row.
     * The source item is picked from the flattened backordered rows via
     * `.find` (not `[0]`) because a backordered KIT row's flattened items
     * include non-backordered sibling components. Unlike HD's always-plural
     * notice, this notice's title is singular/plural based on the
     * Backordered block's own row count (`backordered.length > 1`) — one
     * backordered product/kit row gets "This item…", two or more get
     * "These items…".
     */
    const renderDsOrDs3plBlocks = (group: 'DS' | 'DS3PL') => {
        const items = buckets[group];
        const deliveryRows = buildDeliveryRows(items);
        const { available, backordered } = splitDeliveryRowsByBackorderStatus(deliveryRows);

        if (backordered.length === 0) {
            return [
                renderGroupBlock({
                    group,
                    sectionKey: group.toLowerCase(),
                    rows: deliveryRows,
                    deliveryDate: formatDeliveryDate(
                        getGroupDeliveryDate(group, items, deliveryShipment),
                        i18n.language
                    ),
                    showPerRowNotice: true,
                }),
            ];
        }

        const blocks = [];
        if (available.length > 0) {
            blocks.push(
                renderGroupBlock({
                    group,
                    sectionKey: group.toLowerCase(),
                    rows: available,
                    deliveryDate: formatDeliveryDate(
                        getGroupDeliveryDate(group, flattenDeliveryRowItems(available), deliveryShipment),
                        i18n.language
                    ),
                    showPerRowNotice: true,
                })
            );
        }
        const backorderedFlatItems = flattenDeliveryRowItems(backordered);
        const backorderGroupLevelSourceItem =
            backorderedFlatItems.find((item) => getAtpItemFields(item).isOnBackOrder) ?? backorderedFlatItems[0];
        blocks.push(
            renderGroupBlock({
                group,
                sectionKey: `${group.toLowerCase()}-backordered`,
                rows: backordered,
                deliveryDate: null,
                showPerRowNotice: false,
                groupLevelNoticeSourceItem: backorderGroupLevelSourceItem,
                groupLevelNoticePlural: backordered.length > 1,
            })
        );
        return blocks;
    };

    return (
        <div className={className} aria-labelledby="order-review-items-heading" data-testid="order-review-items">
            <Typography variant="h4" as="div" id="order-review-items-heading" className="sr-only">
                {t('reviewOrder.itemsHeading')}
            </Typography>

            <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3 md:divide-x md:divide-border">
                <OrderReviewShipTo
                    className="md:pr-6"
                    address={deliveryShipment.shippingAddress}
                    email={contactEmail}
                />
                <OrderReviewBillTo className="md:pr-6" address={billingAddress} />
                <OrderReviewPaymentMethod instrument={paymentInstrument} />
            </div>

            {/* Products Grouped by Delivery Method */}
            <div className="flex flex-col gap-8">
                {visibleGroups.flatMap((group) => (group === 'HD' ? [renderHdBlock()] : renderDsOrDs3plBlocks(group)))}
            </div>
        </div>
    );
}
